/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { resolveName, SimNaoEnum, SimNaoRadioGroupField, TextField } from 'components/form'
import { RequiredLabel } from 'components/form/final-form/RequiredLabel/RequiredLabel'
import { isEmpty } from 'lodash'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoOutroFieldModel } from '../model-consultaSeguimento'

export type ExemplosHorizontalColumnFieldComponents = {
  Append?: JSX.Element
}

interface ExemplosHorizontalColumnFieldProps<T extends string> {
  name: MetaPath<Record<T, SimNaoEnum>>
  nameOutro?: MetaPath<ConsultaSeguimentoOutroFieldModel>
  label: string
  options: { value: T; label: string }[]
  required?: boolean
  components?: ExemplosHorizontalColumnFieldComponents
  showIndicator?: boolean
  showTagOptional?: boolean
  hasOutro?: boolean
}

export function ExemplosHorizontalColumnField<T extends string>(
  props: Readonly<ExemplosHorizontalColumnFieldProps<T>>
) {
  const { name, nameOutro, label, required, options, components, showIndicator, showTagOptional, hasOutro } = props

  const {
    input: { value },
  } = useField<Record<T, SimNaoEnum>>(resolveName(name), {
    subscription: { value: true },
  })

  const {
    input: { value: outroFieldValue },
  } = useField<string>(resolveName(nameOutro.valor), { subscription: { value: true } })

  const theme = useTheme()
  const styles = createStyles(theme, showIndicator)

  return (
    <div css={styles.root}>
      <div css={styles.header}>
        {required ? <RequiredLabel fontWeight='bold' label={label} /> : <Text fontWeight='bold'>{label}</Text>}
      </div>
      <VFlow vSpacing={0}>
        <VFlow vSpacing={0} style={styles.list}>
          {options.map((option) => {
            const mostrarOpcional = showTagOptional && value?.[option.value] !== SimNaoEnum.SIM

            return (
              <VFlow key={option.value} style={styles.listItem}>
                <Text>
                  {option.label}
                  {mostrarOpcional && (
                    <span css={styles.tagOpcional}>
                      <span css={styles.tagOpcionalIconWrapper}>
                        <Icon size={1} icon='infoCircleOutline' />
                      </span>
                      <Text color='inherit'>Opcional</Text>
                    </span>
                  )}
                </Text>
                <SimNaoRadioGroupField name={name[option.value.toString()]} hSpacing={0.5} />
              </VFlow>
            )
          })}
        </VFlow>
        {hasOutro && (
          <VFlow style={styles.outro}>
            <TextField
              name={nameOutro.valor}
              label={<Text fontWeight='normal'>Outro:</Text>}
              maxLength={150}
              placeholder='Descrever, se houver'
            />
            <SimNaoRadioGroupField name={nameOutro.selecionado} hSpacing={0.5} disabled={isEmpty(outroFieldValue)} />
          </VFlow>
        )}
        {components?.Append && <div css={styles.appendWrapper}>{components?.Append}</div>}
      </VFlow>
    </div>
  )
}

const createStyles = (theme: Theme, showIndicator: boolean) => ({
  root: css`
    border-radius: 2px;
    border: 1px solid ${theme.pallete.divider};
    box-shadow: 0px 0px 0px ${showIndicator ? 2 : 0}px ${theme.pallete.primary.c60};
  `,
  header: css`
    padding: 0.5rem 1rem;
    background: ${theme.pallete.gray.c90};
  `,
  list: css`
    & > div:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
  listItem: css`
    padding: 0.75rem 1rem;
  `,
  appendWrapper: css`
    padding: 0 1rem;
  `,
  tagOpcional: css`
    color: ${theme.pallete.primary.main};
    margin-left: 0.75rem;
  `,
  tagOpcionalIconWrapper: css`
    margin-right: 0.25rem;
    svg {
      margin-bottom: -0.25rem;
    }
  `,
  outro: css`
    padding: 0.75rem 1rem;
    border-top: 1px solid ${theme.pallete.divider};
  `,
})
